import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateGuideline } from '../api/guidelines.api';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import type { GuidelineResponseDto, GuidelineUpdateDto } from '@/lib/api/guidelines-client';
import type { Category } from '@inference.engine/guidelines/web';
import { useTranslation } from 'react-i18next';

export interface UpdateGuidelineParams {
  guidelineId: string;
  name: string;
  description: string;
  category: Category;
  folderId?: string;
}

export const useUpdateGuideline = () => {
  const { t } = useTranslation('knowledge');
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId();

  return useMutation<GuidelineResponseDto | null, Error, UpdateGuidelineParams>({
    mutationFn: async ({ guidelineId, name, description, category }) => {
      // `examples` and `assetIds` are deliberately ABSENT, not empty. This modal
      // edits a name, a description and a category; it has no opinion about either
      // collection, and the endpoint reads an empty array as "clear these" — which
      // is how renaming a guideline used to wipe its examples.
      const updateDto: GuidelineUpdateDto = { name, description, category };
      return updateGuideline(guidelineId, updateDto);
    },
    onSuccess: (data, variables) => {
      if (variables.folderId) {
        void queryClient.invalidateQueries({
          queryKey: ['guidelines', 'folder', variables.folderId],
        });
      }
      void queryClient.invalidateQueries({ queryKey: ['folders', workspaceId] });
      if (data) {
        notificationService.showSuccess(
          t('guidelines.useUpdateGuideline.guidelineUpdated', { name: data.name }),
          {
            anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
          },
        );
      }
    },
    onError: (error) => {
      notificationService.showError(
        error.message || t('guidelines.useUpdateGuideline.failedToUpdateGuideline'),
      );
    },
  });
};
